import React from 'react';
import {
    Box,
    Grid,
    TextField,
    MenuItem,
    Button,
    Typography,
    Snackbar,
    Alert,
    CircularProgress,
    Paper,
    Tooltip,
    IconButton,
    Chip,
    InputAdornment,
    Divider
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import SaveIcon from '@mui/icons-material/Save';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

import './FinancialSettings.scss';
import { useOidcAccessToken } from "@axa-fr/react-oidc";
import { createProviderConfiguration, getProviderConfiguration, updateProviderConfiguration } from '../../services/ProviderConfiguration';


const PROVIDERS = [
    { value: 'fiserv', label: 'Fiserv' }
];

const GATEWAYS: Record<string, { value: string; label: string }[]> = {
    fiserv: [
        { value: 'cardpointe', label: 'CardPointe' }
    ]
};

const ENVIRONMENTS = [
    { value: 'prod', label: 'Production' },
    { value: 'uat', label: 'Testing (UAT)' }
];

const DEFAULT_FORM = {
    provider: 'fiserv',
    gateway: 'cardpointe',
    environment: 'uat',
    merchantId: '',
    username: '',
    password: '',
};

const FinancialSettings: React.FC<any> = () => {

    const [form, setForm] = React.useState<any>({ ...DEFAULT_FORM });
    const [originalForm, setOriginalForm] = React.useState<any>(null);
    const [showPassword, setShowPassword] = React.useState(false);
    const [submitCount, setSubmitCount] = React.useState(0);

    const { accessToken } = useOidcAccessToken();

    const [loading, setLoading] = React.useState(true);
    const [saving, setSaving] = React.useState(false);
    const [isEditMode, setIsEditMode] = React.useState(false);
    const [snackbar, setSnackbar] = React.useState<any>({
        open: false, message: '', type: 'success'
    });

    const isEmpty = (key: string): boolean => !form[key] || form[key].toString().trim() === '';

    const showError = (key: string): boolean => submitCount > 0 && isEmpty(key);

    const helperText = (key: string, label: string): string =>
        showError(key) ? `${label} is required` : '';

    const getFieldValidationError = (key: string): string => {
        const value = form[key]?.toString().trim() || '';

        if (submitCount === 0 && !value) return '';

        if (!value) return `${key === 'merchantId' ? 'Merchant ID' : key.charAt(0).toUpperCase() + key.slice(1)} is required`;

        if (key === 'merchantId') {
            if (!/^\d+$/.test(value)) return 'Merchant ID must be numeric';
            if (value.length < 6 || value.length > 20) return 'Merchant ID must be 6–20 digits';
        }

        return '';
    };

    const isFormValid = (): boolean =>
        ['provider', 'gateway', 'environment', 'merchantId', 'username', 'password']
            .every(k => !isEmpty(k));

    const isDirty = (): boolean => {
        if (!originalForm) return true;
        return Object.keys(DEFAULT_FORM).some(k => form[k] !== originalForm[k]);
    };

    const loadConfig = async () => {
        try {
            const res = await getProviderConfiguration(accessToken);
            if (res?.data) {
                const parsed = typeof res.data === 'string' ? JSON.parse(res.data) : res.data;
                if (Object.keys(parsed).length === 0) {
                    setIsEditMode(false);
                } else {
                    const loaded = {
                        provider: parsed.provider || 'fiserv',
                        gateway: parsed.gateway || 'cardpointe',
                        environment: parsed.environment || 'uat',
                        merchantId: parsed.merchantId || '',
                        username: parsed.username || '',
                        password: parsed.password || ''
                    };
                    setForm(loaded);
                    setOriginalForm({ ...loaded });
                    setIsEditMode(true);
                }
            } else {
                setIsEditMode(false);
            }
        } catch (err: any) {
            setIsEditMode(false);
        } finally {
            setLoading(false);
        }
    };

    React.useEffect(() => {
        if (accessToken) loadConfig();
    }, [accessToken]);

    const handleChange = (key: string, value: any) => {
        setForm((prev: any) => ({ ...prev, [key]: value }));
    };

    // Submit handler for both create and update
    const handleSubmit = async () => {
        if (saving) return;
        setSubmitCount(prev => prev + 1);

        if (!isFormValid()) {
            setSnackbar({
                open: true,
                message: 'Please fill all mandatory fields before saving',
                type: 'warning'
            });
            return;
        }

        setSaving(true);
        try {
            const configKey = `subscription.${form.provider.toLowerCase()}.${form.gateway.toLowerCase()}.config`;
            const payload = { key: configKey, data: JSON.stringify(form) };

            const response = isEditMode
                ? await updateProviderConfiguration(payload, accessToken)
                : await createProviderConfiguration(payload, accessToken);

            const result = response?.data;

            if (result === 'Inserted' || result === 'Updated') {
                setSnackbar({
                    open: true,
                    message: result === 'Updated'
                        ? 'Configuration updated successfully'
                        : 'Configuration created successfully',
                    type: 'success'
                });
                setIsEditMode(true);
                setOriginalForm({ ...form });
                await loadConfig();
            } else {
                const errorMsg =
                    response?.data?.message ||
                    response?.data?.description ||
                    response?.data ||
                    'Failed to save configuration';

                setSnackbar({
                    open: true,
                    message: typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg),
                    type: 'error'
                });

                return;
            }
        } catch (error: any) {
            setSnackbar({
                open: true,
                message:
                    error?.response?.data?.message ||
                    error?.response?.data?.description ||
                    error?.message ||
                    'Failed to save. Please try again.',
                type: 'error'
            });
        } finally {
            setSaving(false);
        }
    };

    const handleReset = () => {
        setForm({ ...DEFAULT_FORM });
        setSubmitCount(0);
    };

    // Loading state
    if (loading) {
        return (
            <Box className="financial-settings-loader">
                <CircularProgress />
                <Typography variant="body2" className="financial-settings-loader-text">Loading configuration...</Typography>
            </Box>
        );
    }

    return (

        <Box className="financial-settings-root">
            <Alert
                severity={isEditMode ? 'info' : 'warning'}
                icon={isEditMode ? <CheckCircleOutlineIcon fontSize="small" /> : <ErrorOutlineIcon fontSize="small" />}
                className="financial-settings-mode-banner"
                classes={{ icon: 'financial-settings-mode-icon' }}
            >
                {isEditMode
                    ? 'You are updating your payment settings.'
                    : 'Set up payment settings to enable subscription billing.'}
            </Alert>
            {/* Payment Gateway Configuration Card */}
            <Paper className="financial-settings-card" variant="outlined">
                <Box className="financial-settings-card-header">
                    <Typography className="financial-settings-card-title">Payment Gateway Configuration</Typography>
                    <Typography className="financial-settings-card-desc">
                        Select the payment provider, gateway, and environment for subscription billing
                    </Typography>
                </Box>
                <Divider sx={{ mb: 2.5 }} className="financial-settings-divider" />

                <Grid container spacing={2.5} className="financial-settings-grid">
                    <Grid item xs={12} sm={6}>
                        <TextField select label="Payment Provider" fullWidth required size="small"
                            className="financial-settings-textfield"
                            value={form.provider}
                            onChange={(e) => handleChange('provider', e.target.value)}
                            error={showError('provider')}
                            helperText={helperText('provider', 'Payment Provider')}
                        >
                            {PROVIDERS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField select label="Payment Gateway" fullWidth required size="small"
                            className="financial-settings-textfield"
                            value={form.gateway}
                            onChange={(e) => handleChange('gateway', e.target.value)}
                            error={showError('gateway')}
                            helperText={helperText('gateway', 'Payment Gateway')}
                        >
                            {(GATEWAYS[form.provider] || []).map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField select label="Environment" fullWidth required size="small"
                            className="financial-settings-textfield"
                            value={form.environment}
                            onChange={(e) => handleChange('environment', e.target.value)}
                            error={showError('environment')}
                            helperText={helperText('environment', 'Environment')}
                        >
                            {ENVIRONMENTS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                        </TextField>
                    </Grid>
                </Grid>
            </Paper>

            {/* CardPointe Credentials Card */}
            <Paper className="financial-settings-card" variant="outlined">
                <Box className="financial-settings-card-header">
                    <Typography className="financial-settings-card-title">
                        CardPointe Credentials <span className="financial-settings-required-asterisk">*</span>
                    </Typography>
                    <Typography className="financial-settings-card-desc">
                        Enter your Fiserv CardPointe merchant credentials to enable subscription payments
                    </Typography>
                </Box>
                <Divider sx={{ mb: 2.5 }} className="financial-settings-divider" />

                <Grid container spacing={2.5} className="financial-settings-grid">
                    <Grid item xs={12} sm={4}>
                        <TextField label="Merchant ID" fullWidth required size="small"
                            className="financial-settings-textfield"
                            value={form.merchantId}
                            onChange={(e) => handleChange('merchantId', e.target.value)}
                            error={!!getFieldValidationError('merchantId')}
                            helperText={getFieldValidationError('merchantId')}
                            placeholder="e.g. 800000005245"
                            inputProps={{ maxLength: 20, minLength: 6, pattern: '[0-9]*', inputMode: 'numeric' }}
                            onKeyDown={(e) => {
                                const allowed = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Home', 'End'];
                                if (!allowed.includes(e.key) && !/^\d$/.test(e.key)) e.preventDefault();
                            }}
                            InputProps={{
                                endAdornment: (
                                    <Tooltip title="Your unique merchant ID from the CardPointe Merchant Portal" arrow>
                                        <IconButton size="small" edge="end" tabIndex={-1}>
                                            <InfoOutlinedIcon fontSize="small" className="financial-settings-info-icon" />
                                        </IconButton>
                                    </Tooltip>
                                )
                            }}
                        />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <TextField label="Username" fullWidth required size="small"
                            className="financial-settings-textfield"
                            value={form.username}
                            onChange={(e) => handleChange('username', e.target.value.replace(/\s/g, ''))}
                            onKeyDown={(e) => {
                                if (e.key === ' ') e.preventDefault();
                            }}
                            error={!!getFieldValidationError('username')}
                            helperText={getFieldValidationError('username')}
                            placeholder="CardPointe portal username"
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField label="Password" fullWidth required size="small"
                            className="financial-settings-textfield"
                            type={showPassword ? 'text' : 'password'}
                            value={form.password}
                            onChange={(e) => handleChange('password', e.target.value.replace(/\s/g, ''))}
                            onKeyDown={(e) => {
                                if (e.key === ' ') e.preventDefault();
                            }}
                            error={!!getFieldValidationError('password')}
                            helperText={getFieldValidationError('password')}
                            placeholder="CardPointe portal password"
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small" tabIndex={-1}>
                                            {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Grid>
                </Grid>
            </Paper>

            {/* ACTION BAR */}
            <Box className="financial-settings-actions">
                <Button variant="contained" onClick={handleSubmit}
                    disabled={saving || !isFormValid() || (isEditMode && !isDirty())}
                    disableElevation
                    startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                    className="financial-settings-save-btn"
                >
                    {saving ? 'Saving...' : isEditMode ? 'Update Configuration' : 'Save Configuration'}
                </Button>
                <Button variant="outlined" onClick={handleReset} startIcon={<RestartAltIcon />} className="financial-settings-reset-btn">
                    Reset
                </Button>
            </Box>

            {/* Snackbar */}
            <Snackbar open={snackbar.open} autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                className="financial-settings-snackbar"
            >
                <Alert severity={snackbar.type} onClose={() => setSnackbar({ ...snackbar, open: false })}
                    variant="filled" className="financial-settings-snackbar-alert"
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default FinancialSettings;
